import { styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, useTheme } from '@mui/material';
import { format } from 'date-fns';
import React from 'react';
import EmptyState from 'src/frontend-common/components/EmptyState';
import Row from 'src/frontend-common/components/Row';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';
import { CompanyUsage, Payment, Product } from 'src/redux/services/adminDashboard';
import { fixedDigitsFormat } from 'src/utils/number';
import TableSkeleton from './TableSkeleton';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import Crif from './Icons/Crif.png';
import Facile from './Icons/Facile.png';
import Nexi from './Icons/Nexi.png';
import Scalapay from './Icons/Scalapay.png';
import Sella from './Icons/Sella.png';
import Sisal from './Icons/Sisal.png';
import Snaitech from './Icons/Snaitech.png';
import Younited from './Icons/Younited.png';
import Trulioo from './Icons/Trulioo.png';
import Lastminute from './Icons/Lastminute.png';
import Decathlon from './Icons/Decathlon.png';
import Mavriq from './Icons/Mavriq.png';
import Monnai from './Icons/Monnai.png';
import MotorK from './Icons/MotorK.png';
import Pledg from './Icons/Pledg.png';
import Placeholder from './Icons/Placeholder.png';
import FailureScreen from 'src/frontend-common/components/FailureScreen';
import Verisoul from './Icons/Verisoul.png';
import ING from './Icons/ING.png';
import Elavon from './Icons/Elavon.png';
import Agos from './Icons/agos.png';
import Cofidis from './Icons/cofidis.png';
import Fiditalia from './Icons/fiditalia.png';
import IdMerit from './Icons/id-merit.png';
import Italo from './Icons/italo.png';
import TmtId from './Icons/tmt-id.png';
import TrustDecision from './Icons/trust-decision.png';
import PrimeInsight from './Icons/prime-insight.png';
import { dotsFormat } from 'src/frontend-common/utils/functions';

interface AdminDashboardTableProps {
  tableCells: string[];
  data: AdminTableData[];
  dataType: DataType;
  isLoading?: boolean;
  isEmpty?: boolean;
  isError?: boolean;
}

const TableRowWrapper = styled(TableRow)(() => ({
  height: '63px !important',
  '&:hover': {
    backgroundColor: '#F3F4F6',
  },
  '& td': {
    paddingTop: 8,
    paddingBottom: 8,
  },
}));
type AdminTableData = CompanyUsage | Product | Payment;

type DataType = 'company' | 'product' | 'payment';

const AdminDashboardTable: React.FC<AdminDashboardTableProps> = ({ tableCells, data, dataType, isLoading, isEmpty, isError }) => {
  const [page, setPage] = React.useState(0);
  const [orderBy, setOrderBy] = React.useState<string | null>(null);
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');
  const rowsPerPage = 8;
  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = () => {
    setPage(0);
  };

  const claimMapping = (claim: 'phone' | 'name' | 'email' | 'domain' | 'ip' | 'score' | 'bot_detection' | 'login') => {
    switch (claim) {
      case 'phone':
        return 'Phone';
      case 'login':
        return 'Login';
      case 'name':
        return 'Name';
      case 'email':
        return 'Email';
      case 'domain':
        return 'Domain';
      case 'ip':
        return 'IP Address';
      case 'score':
        return 'Onboarding';
      case 'bot_detection':
        return 'Session';
      default:
        return 'Unknown';
    }
  };

  const theme = useTheme();

  const imgHeight = 32;
  const getIcon = (value: string) => {
    switch (value.toLowerCase()) {
      case 'crif':
        return <img src={Crif} style={{ height: imgHeight }} />;
      case 'facile.it':
      case 'facile.it prestiti':
        return <img src={Facile} style={{ height: imgHeight }} />;
      case 'elavon':
        return <img src={Elavon} style={{ height: imgHeight }} />;
      case 'cofidis sa':
        return <img src={Cofidis} style={{ height: imgHeight }} />;
      case 'fiditalia':
        return <img src={Fiditalia} style={{ height: imgHeight }} />;
      case 'id merit':
        return <img src={IdMerit} style={{ height: imgHeight }} />;
      case 'agos recupero':
      case 'agos frodi':
        return <img src={Agos} style={{ height: imgHeight }} />;
      case 'italo':
        return <img src={Italo} style={{ height: imgHeight }} />;
      case 'tmt analysis':
        return <img src={TmtId} style={{ height: imgHeight }} />;
      case 'trust decision':
      case 'trust decision delete':
        return <img src={TrustDecision} style={{ height: imgHeight }} />;
      case 'nexi':
        return <img src={Nexi} style={{ height: imgHeight }} />;
      case 'scalapay':
        return <img src={Scalapay} style={{ height: imgHeight }} />;
      case 'banca sella':
        return <img src={Sella} style={{ height: imgHeight }} />;
      case 'sisal':
        return <img src={Sisal} style={{ height: imgHeight }} />;
      case 'snaitech':
        return <img src={Snaitech} style={{ height: imgHeight }} />;
      case 'younited':
        return <img src={Younited} style={{ height: imgHeight }} />;
      case 'trulioo':
        return <img src={Trulioo} style={{ height: imgHeight }} />;
      case 'lastminute':
        return <img src={Lastminute} style={{ height: imgHeight }} />;
      case 'decathlon':
        return <img src={Decathlon} style={{ height: imgHeight }} />;
      case 'mavriq':
        return <img src={Mavriq} style={{ height: imgHeight }} />;
      case 'monnai':
        return <img src={Monnai} style={{ height: imgHeight }} />;
      case 'motork':
        return <img src={MotorK} style={{ height: imgHeight }} />;
      case 'pledg':
        return <img src={Pledg} style={{ height: imgHeight }} />;
      case 'verisoul':
        return <img src={Verisoul} style={{ height: imgHeight }} />;
      case 'ing bank - it':
        return <img src={ING} style={{ height: imgHeight }} />;
      case 'prime insight':
        return <img src={PrimeInsight} style={{ height: imgHeight }} />;
      default:
        return <img src={Placeholder} style={{ height: imgHeight }} />;
    }
  };

  const renderRow = (item: AdminTableData) => {
    switch (dataType) {
      case 'company':
        const t1 = item as CompanyUsage;
        return (
          <>
            <TableCell align="left">
              <Row style={{ alignItems: 'center', gap: theme.spacing(1) }}>
                {getIcon(t1.company.name)}
                <>{t1.company.name}</>
              </Row>
            </TableCell>
            <TableCell align="left">{dotsFormat(t1.lookups)}</TableCell>
            <TableCell align="left">{`${useGetCurrency()} ${fixedDigitsFormat(t1.credits, 2)}`}</TableCell>
            <TableCell align="left" style={{ textAlign: 'start' }}>{`${useGetCurrency()} ${fixedDigitsFormat(t1.balance, 2)}`}</TableCell>
          </>
        );
      case 'product':
        const t2 = item as Product;
        return (
          <>
            <TableCell align="left">{claimMapping(t2.claim)}</TableCell>
            <TableCell align="left">{dotsFormat(t2.lookups)}</TableCell>
            <TableCell align="left" style={{ textAlign: 'start' }}>{`${useGetCurrency()} ${fixedDigitsFormat(t2.credits, 2)}`}</TableCell>
          </>
        );
      case 'payment':
        const t3 = item as Payment;
        return (
          <>
            <TableCell align="left">
              <Row style={{ alignItems: 'center', gap: theme.spacing(1) }}>
                {getIcon(t3.company_name)}
                <>{t3.company_name}</>
              </Row>
            </TableCell>
            <TableCell align="left">{t3.app_key}</TableCell>
            <TableCell align="left"> {`${useGetCurrency()} ${fixedDigitsFormat(parseInt(t3.value), 2)}`}</TableCell>
            <TableCell align="left" style={{ textAlign: 'start' }}>
              {format(new Date(t3.expiration_date), 'yyyy-MM-dd')}
            </TableCell>
          </>
        );
      default:
        return null;
    }
  };
  if (isError) return <FailureScreen>Something went wrong. Please refresh the page and try again.</FailureScreen>;
  if (isEmpty) return <EmptyState />;
  if (isLoading) return <TableSkeleton />;
  return (
    <>
      <TableContainer sx={{ minHeight: dataType !== 'payment' ? 540 : 'none' }}>
        <Table>
          <TableHead>
            <TableRow>
              {tableCells.map((header) => {
                const headerToKeyMap: Record<string, string> = {
                  Company: 'company',
                  Lookups: 'lookups',
                  Usage: 'usage',
                  Balance: 'balance',
                  // altri mapping se servono
                };

                const headerKey = headerToKeyMap[header] || header.toLowerCase();
                const sortableFieldsByType: Record<DataType, string[]> = {
                  company: ['company', 'lookups', 'usage', 'balance'],
                  product: ['amount'],
                  payment: ['company_name', 'value', 'expiration_date'],
                };

                const isSortable = sortableFieldsByType[dataType]?.includes(headerKey);
                const isActive = orderBy === headerKey;

                return (
                  <TableCell
                    align="left"
                    key={header}
                    onClick={() => {
                      if (isSortable) {
                        if (isActive) {
                          setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setOrderBy(headerKey);
                          setOrder('asc');
                        }
                      }
                    }}
                    style={{ cursor: isSortable ? 'pointer' : 'default' }}
                  >
                    <Row>
                      <>
                        {header}
                        {isSortable &&
                          (isActive ? (
                            order === 'asc' ? (
                              <ArrowUpward
                                sx={{
                                  fontSize: '18px',
                                  marginLeft: theme.spacing(1),
                                  color: theme.palette.text.secondary,
                                }}
                              />
                            ) : (
                              <ArrowDownward
                                sx={{
                                  fontSize: '18px',
                                  marginLeft: theme.spacing(1),
                                  color: theme.palette.text.secondary,
                                }}
                              />
                            )
                          ) : (
                            <ArrowDownward
                              sx={{
                                fontSize: '18px',
                                marginLeft: theme.spacing(1),
                                color: theme.palette.text.secondary,
                              }}
                            />
                          ))}
                      </>
                    </Row>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <TableBody style={{ backgroundColor: 'white' }}>
            {[...data]
              .sort((a, b) => {
                if (!orderBy) return 0;

                let aVal: any;
                let bVal: any;

                switch (dataType) {
                  case 'company':
                    const ca = a as CompanyUsage;
                    const cb = b as CompanyUsage;
                    if (orderBy === 'balance') {
                      aVal = ca.balance;
                      bVal = cb.balance;
                    } else if (orderBy === 'lookups') {
                      aVal = ca.lookups;
                      bVal = cb.lookups;
                    } else if (orderBy === 'usage') {
                      aVal = ca.credits;
                      bVal = cb.credits;
                    } else if (orderBy === 'company') {
                      aVal = ca.company.name.toLowerCase();
                      bVal = cb.company.name.toLowerCase();
                    }
                    break;

                  case 'product':
                    const pa = a as Product;
                    const pb = b as Product;
                    if (orderBy === 'lookups') {
                      aVal = pa.lookups;
                      bVal = pb.lookups;
                    } else if (orderBy === 'amount') {
                      aVal = pa.credits;
                      bVal = pb.credits;
                    }
                    break;

                  case 'payment':
                    const pma = a as Payment;
                    const pmb = b as Payment;
                    if (orderBy === 'company_name') {
                      aVal = pma.company_name.toLowerCase();
                      bVal = pmb.company_name.toLowerCase();
                    } else if (orderBy === 'value') {
                      aVal = parseFloat(pma.value);
                      bVal = parseFloat(pmb.value);
                    } else if (orderBy === 'expiration_date') {
                      aVal = new Date(pma.expiration_date).getTime();
                      bVal = new Date(pmb.expiration_date).getTime();
                    }
                    break;
                }

                if (aVal == null || bVal == null) return 0;

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                  return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                }

                return order === 'asc' ? aVal - bVal : bVal - aVal;
              })
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item, index) => (
                <TableRowWrapper key={index}>{renderRow(item)}</TableRowWrapper>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        style={{ backgroundColor: 'white' }}
        count={data.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};

export default AdminDashboardTable;
